import { createListenerMiddleware } from "@reduxjs/toolkit"
import { commandFailure, StaffCommandNotSentError } from "@/services/staffCommands"
import { currentStaffSession, matchesStaffSession, staffSessionActions } from "@/store/staffSessionSlice"
import { benefitCreationActions as actions, type BenefitCreationState, type CreationAttempt } from "./benefitCreationSlice"
import type { benefitApi } from "./benefitApi"

type State = { benefitCreation: BenefitCreationState }
/** A listener, not a mounted component, owns each accepted immutable creation attempt. No replay. */
export function createBenefitCreationListener(send: (attempt: CreationAttempt) => Promise<unknown>, client?: Pick<typeof benefitApi, "util">) {
  const listener = createListenerMiddleware()
  listener.startListening({ actionCreator: actions.attemptRequested, effect: async ({ payload }, api) => {
    const previous = (api.getOriginalState() as State).benefitCreation.attempt
    const accepted = (api.getState() as State).benefitCreation.attempt
    if (!accepted || accepted.id !== payload.id || previous === accepted || previous?.id === accepted.id || accepted.phase !== "saving") return
    const expected = { scopeKey: accepted.scopeKey, epoch: accepted.epoch }
    const session = currentStaffSession(api.getState())
    if (!matchesStaffSession(api.getState(), expected) || session?.phase === "checking" || session?.phase === "unavailable") {
      api.dispatch(actions.attemptRejected({ id: accepted.id, ...commandFailure(new StaffCommandNotSentError("authentication-unavailable")) }))
      return
    }
    try {
      const result = await send(accepted)
      if (!matchesStaffSession(api.getState(), expected)) return
      // A 2xx with no usable ID is not proof the creation failed. Never offer a blind retry.
      if (!result || typeof result !== "object" || !("id" in result) || typeof result.id !== "string" || !result.id.trim()) throw new Error("Creation response missing its identifier")
      api.dispatch(actions.attemptConfirmed({ id: accepted.id, resultId: result.id }))
      if (client) api.dispatch(client.util.invalidateTags([{ type: "BenefitCreationRead", id: accepted.scopeKey }]))
    } catch (error) {
      if (!matchesStaffSession(api.getState(), expected)) return
      const failure = commandFailure(error)
      if (failure.outcome === "denied") {
        // Resource/access loss purges protected creation drafts as well as cached previews.
        api.dispatch(staffSessionActions.scopeRevoked())
        return
      }
      api.dispatch(actions.attemptRejected({ id: accepted.id, ...failure,
        message: failure.outcome === "conflict" ? "Creation was not accepted because the source or person changed. The original draft and confirmation are retained. Discard and review fresh data before trying again." : failure.message }))
    }
  } })
  return listener
}
